import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Event,
  EventConfigClient,
  Marketplace,
  SellerEventMarketplaceEventInfo,
} from 'src/WebApiController';

export const useMarketplaceEventInfo = (event?: Event) => {
  const { activeAccountWebClientConfig, loginContext } = useAppContext();
  const { trackError } = useClientLogContext();

  const shouldQuery =
    activeAccountWebClientConfig?.activeAccountId != null &&
    Boolean(event?.viagId);
  const marketplaceEventInfoQuery = useQuery({
    queryKey: [
      'EventConfigClient.getSellerEventMarketplaceEventInfo',
      activeAccountWebClientConfig.activeAccountId,
      event?.viagId,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const result = await new EventConfigClient(
            activeAccountWebClientConfig
          ).getSellerEventMarketplaceEventInfo(event?.viagId, event?.mpId);

          if (result) {
            return result.reduce(
              (r, m) => {
                r[m.marketplace] = m;
                return r;
              },
              {} as Record<string, SellerEventMarketplaceEventInfo>
            );
          }

          return {};
        },
        (error) => {
          trackError(
            'EventConfigClient.getSellerEventMarketplaceEventInfo',
            error,
            {
              viagogoEntityId: event?.viagId,
              viagogoMappingId: event?.mpId,
            }
          );
        }
      );
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });

  const marketplaceEventInfo = useMemo(
    () =>
      loginContext?.user?.activeAccount.marketplaceSettings
        .filter((m) => m.mkp !== Marketplace.Automatiq) // Automatiq is not an actual Marketplace for Event viewing
        .map((m) => {
          const minfo = marketplaceEventInfoQuery.data?.[m.mkp];
          return (
            minfo ??
            ({
              marketplace: m.mkp,
              viagogoEventId: event?.viagId,
              mappedEventId:
                m.mkp === Marketplace.StubHub ? event?.viagId : undefined,
            } as SellerEventMarketplaceEventInfo)
          );
        }),
    [
      event?.viagId,
      loginContext?.user?.activeAccount.marketplaceSettings,
      marketplaceEventInfoQuery.data,
    ]
  );

  return {
    marketplaceEventInfo,
    refetchMarketplaceEventInfo: marketplaceEventInfoQuery.refetch,
  };
};
